import FormContainer from "@app/components/forms/container/FormContainer";
import { useFormWithSchema } from "@app/components/forms/hooks/useFormWithSchema";
import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import { GroupAddSchema } from "@app/components/modals/GroupModal/groupAddSchema";
import {
  Button,
  ModalContent,
  ModalProps,
  createModalComponent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogClose,
  useModal,
} from "@shortsworks/ui";
import {
  useCreateStoryGroup,
  useGetStoryGroupList,
  useDeleteStoryGroup,
  useUpdateStoryGroup,
} from "@shortsworks/user-api";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

const GroupAddContent = (props: ModalProps) => {
  const { showModal } = useModal();

  const queryClient = useQueryClient();
  const { queryKey: getStoryGroupListQueryKey } = useGetStoryGroupList();
  const createStoryGroup = useCreateStoryGroup({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          queryKey: getStoryGroupListQueryKey,
        });
      },
    },
  });

  const { form, schemas, parseForm } = useFormWithSchema({
    formSchemas: GroupAddSchema,
    mode: "onChange",
  });

  // onError, onSuccess 형태로 관리하기
  const addGroup = async () => {
    const parsedForm = parseForm();
    if (!parsedForm.success) return;
    const { name } = parsedForm.data;
    try {
      const result = await createStoryGroup.mutateAsync({ data: { name } });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "그룹 추가에 실패했습니다.",
        });
      }
      props.close();

      // 에러로 넘어갈 때 type 명확하지 않는 점 수정 필요
    } catch (error: any) {
      if (error.error.code === "8001") {
        return showModal(CommonErrorModal, {
          title: "그룹 이름이 이미 존재합니다.",
        });
      }
    }
  };

  return (
    <ModalContent>
      <DialogHeader>
        <DialogTitle>그룹 추가</DialogTitle>
        <DialogDescription>
          <FormContainer className="mt-2" schemas={schemas} form={form} />
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild></DialogClose>
        <Button className="p-0 w-[104px] h-[40px]" onClick={addGroup}>
          확인
        </Button>
      </DialogFooter>
    </ModalContent>
  );
};

interface GroupUpdateContentProps {
  id: string;
  name: string;
}
const GroupUpdateContent = (props: ModalProps<GroupUpdateContentProps>) => {
  const { showModal } = useModal();

  const queryClient = useQueryClient();
  const { queryKey: storyGroupListQueryKey } = useGetStoryGroupList();
  const { mutateAsync: mutateAsyncUpdateStoryGroup } = useUpdateStoryGroup({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          queryKey: storyGroupListQueryKey,
        });
      },
    },
  });

  const { form, schemas, parseForm, setFormValues } = useFormWithSchema({
    formSchemas: GroupAddSchema,
    mode: "onChange",
  });

  useEffect(() => {
    setFormValues({
      name: props.name,
    });
  }, []);

  const updateGroup = async () => {
    const parsedForm = parseForm();
    if (!parsedForm.success) return;
    const { name } = parsedForm.data;

    if (name === props.name) {
      return showModal(CommonErrorModal, { title: "그룹 이름이 동일합니다." });
    }

    try {
      const result = await mutateAsyncUpdateStoryGroup({
        data: { groupId: props.id, name },
      });
      if (!result.data.success) {
        return showModal(CommonErrorModal, {
          title: "그룹 변경에 실패했습니다.",
        });
      }
      props.close();

      // 에러로 넘어갈 때 type 명확하지 않는 점 수정 필요
    } catch (error: any) {
      if (error.error.code === "8001") {
        return showModal(CommonErrorModal, {
          title: "그룹 이름이 이미 존재합니다.",
        });
      }
    }
  };

  return (
    <ModalContent>
      <DialogHeader>
        <DialogTitle>그룹 변경</DialogTitle>
        <DialogDescription>
          <FormContainer className="mt-2" schemas={schemas} form={form} />
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild></DialogClose>
        <Button className="p-0 w-[104px] h-[40px]" onClick={updateGroup}>
          확인
        </Button>
      </DialogFooter>
    </ModalContent>
  );
};

interface GroupDeleteContentProps {
  groupId: string;
}

const GroupDeleteContent = (props: ModalProps<GroupDeleteContentProps>) => {
  const { showModal } = useModal();

  const queryClient = useQueryClient();
  const { queryKey: storyGroupListQueryKey } = useGetStoryGroupList();
  const { mutateAsync: mutateAsyncDeleteStoryGroup } = useDeleteStoryGroup({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          queryKey: storyGroupListQueryKey,
        });
      },
    },
  });

  const handleDeleteStoryGroup = async () => {
    try {
      const result = await mutateAsyncDeleteStoryGroup({
        params: { groupId: props.groupId },
      });
      if (!result.data.success) {
        props.close();
        return showModal(CommonErrorModal, {
          title: "그룹 삭제에 실패팼습니다.",
        });
      }
      return props.close();
    } catch (error) {
      props.close();
      return showModal(CommonErrorModal, {
        title: "그룹 삭제에 실패팼습니다.",
      });
    }
  };

  return (
    <ModalContent>
      <DialogHeader>
        <DialogTitle>그룹 삭제</DialogTitle>
        <DialogDescription>그룹을 삭제하시겠습니까?</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild></DialogClose>
        <Button className="p-0 w-[104px] h-[40px]" onClick={props.close}>
          취소
        </Button>
        <Button
          className="p-0 w-[104px] h-[40px]"
          onClick={handleDeleteStoryGroup}
        >
          확인
        </Button>
      </DialogFooter>
    </ModalContent>
  );
};

export const GroupAddModal = createModalComponent(GroupAddContent);
export const GroupUpdateModal = createModalComponent(GroupUpdateContent);
export const GroupDeleteModal = createModalComponent(GroupDeleteContent);
